import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { TutorService } from "src/app/_services/tutor.service";
import {
  AddTutor,
  AddTutorError,
  AddTutorSuccess,
  GetGroupTutorCount,
  GetGroupTutorCountError,
  GetGroupTutorCountSuccess,
  GetTutorTable,
  GetTutorTableError,
  GetTutorTableLength,
  GetTutorTableLengthError,
  GetTutorTableLengthSuccess,
  GetTutorTableSuccess,
  GetTutorUserProfile,
  GetTutorUserProfileError,
  GetTutorUserProfileSuccess,
  ResetTutorTable,
  ResetTutorTableError,
  ResetTutorTableSuccess,
  TutorHasAccessToLearner,
  TutorHasAccessToLearnerError,
  TutorHasAccessToLearnerSuccess,
  UpdateTutorGroupTable,
  UpdateTutorTable,
  UpdateTutorTableError,
  GetCurrentPageNumber,
  GetCurrentPageNumberSuccess,
  GetCurrentPageNumberError,
  UpdateTutorAccountStatus,
  UpdateTutorAccountStatusSuccess,
  UpdateTutorAccountStatusError,
} from "./tutor.actions";
import { catchError, concatMap, map, mergeMap, switchMap, tap, toArray } from "rxjs/operators";
import { Userprofile } from "src/app/_models/Userprofile";
import { HttpErrorResponse } from "@angular/common/http";
import { of } from "rxjs";
import { TutorProfile } from "src/app/_models/Tutor";
import { UpdateLearnerTableDto } from "src/app/_models/UpdateLearnerTableDto";
import { UpdateLearnerTable } from "../learner/learner.actions";
import { Store } from "@ngrx/store";
import { AppConstants } from "src/app/constants/constants";
import { UserProfileService } from "src/app/_services/userprofile.service";

@Injectable()
export class TutorEffects {
  constructor(
    private action$: Actions,
    private tutorService: TutorService,
    private userProfileService: UserProfileService,
    private store: Store
  ) {}

  public addTutorEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(AddTutor),
      switchMap((payload) =>
        this.tutorService.addTutor$(payload.addTutorDto).pipe(
          map((result: Userprofile) => {
            return AddTutorSuccess({ result: result });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(AddTutorError({ crudTutorError: error }));
          })
        )
      )
    )
  );

  public getTutorTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetTutorTable),
      switchMap((payload) =>
        this.tutorService.getTutorTableRecords$(payload.pageNumber, payload.isInitialLoad).pipe(
          map((result: TutorProfile[]) => {
            return GetTutorTableSuccess({ result: result });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetTutorTableError({ tutorTableError: error }));
          })
        )
      )
    )
  );

  public getTutorTableLengthEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetTutorTableLength),
      switchMap(() =>
        this.tutorService.getTutorListLength$().pipe(
          map((length: number) => {
            return GetTutorTableLengthSuccess({ length: length });
          }),
          catchError((error: any) => {
            return of(GetTutorTableLengthError({ error: error }));
          })
        )
      )
    )
  );

  public updateTutorTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(UpdateTutorTable),
      switchMap((payload) =>
        this.tutorService.updateTutorTableRecords$(payload.params).pipe(
          map(() => {
            return GetTutorTable({
              pageNumber: payload.params.pageIndex,
              isInitialLoad: false,
            });
          }),
          catchError((error: any) => {
            return of(UpdateTutorTableError({ error: error }));
          })
        )
      )
    )
  );

  public resetTutorTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(ResetTutorTable),
      switchMap((payload) =>
        this.tutorService.resetTutorTable$().pipe(
          map(() => {
            return ResetTutorTableSuccess({
              isInitialLoad: payload.isInitialLoad,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(ResetTutorTableError({ error: error }));
          })
        )
      )
    )
  );

  public getTutorUserProfileEffect$ = createEffect(() => {
    return this.action$.pipe(
      ofType(GetTutorUserProfile),
      switchMap((payload) => 
        this.tutorService.getTutorUserProfile$(payload.userProfileId).pipe(
          map((result: Userprofile) => {
            return GetTutorUserProfileSuccess({tutorProfile: result});
          }),
          catchError((error: any) => {
            return of(GetTutorUserProfileError({error: error}))
          })
        )
      )
    );
  });

  public updateTutorGroupTableEffect$ = createEffect(() => 
    this.action$.pipe(
      ofType(UpdateTutorGroupTable),
      switchMap((payload) =>
        this.tutorService.updateTutorTableRecords$(payload.params).pipe(
          tap(() => {
            const learnerPayload: UpdateLearnerTableDto = {...payload.params, filterByAccountStatus: AppConstants.ENABLED} as UpdateLearnerTableDto;
            this.store.dispatch(UpdateLearnerTable({params: learnerPayload, isFromTutorPage: false}))
          }),
          map(() => {
            return GetTutorTable({
              pageNumber: payload.params.pageIndex,
              isInitialLoad: false,
            });
          }),
          catchError((error: any) => {
            return of(UpdateTutorTableError({ error: error }));
          })
        )
      )
    )
  );

  public getGroupTutorCountEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetGroupTutorCount),
      switchMap(() =>
        this.tutorService.getGroupTutorCount$().pipe(
          map((result: string) => {
            return GetGroupTutorCountSuccess({
              result: result
            });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetGroupTutorCountError({ error: error }));
          })
        )
      )
    )
  );

  public getTutorHasAccessToLearnerEffect$ = createEffect(() => 
    this.action$.pipe(
      ofType(TutorHasAccessToLearner),
      switchMap((payload) => 
        this.tutorService.isTutorHasAccessToLearner$(payload.learnerId).pipe(
          map((result: boolean) => {
            return TutorHasAccessToLearnerSuccess({result: result});
          }),
          catchError((error: any) => {
            return of(TutorHasAccessToLearnerError({error: error}))
          })
        )
      )
    )
  );

  public getCurrentPageNumberEffect$ = createEffect(() =>
    this.action$.pipe(
        ofType(GetCurrentPageNumber),
        switchMap((payload) =>
            this.tutorService.getCurrentPageNumber$(payload.pageIndex).pipe(
                map((currentPageNumber: number) => {
                    return GetCurrentPageNumberSuccess({ currentPageNumber: currentPageNumber });
                }),
                catchError((error: any) => {
                    return of(GetCurrentPageNumberError({ error: error }));
                })
            )
        )
    )
  );

  public updateTutorAccountStatusEffect$ = createEffect(() => {
    return this.action$.pipe(
      ofType(UpdateTutorAccountStatus),
      mergeMap((data) => {
        return of(...data.param).pipe(
          concatMap(request => this.userProfileService.updateAccountStatus$(request.userProfileIds, request.isDisable)),
          toArray(),
          map((result: boolean[]) => {
            return UpdateTutorAccountStatusSuccess({ isTutorAccountStatusUpdateSuccessful: result.every(success => success) });
          }),
          catchError((err: any) => {
            return of(UpdateTutorAccountStatusError({ error: err }));
          })
        );
      })
    );
  });
}
